import { useState, useMemo } from 'react'
import { useFinance } from '../../context/FinanceContext'
import { getThemeColors } from '../../lib/constants'
import { formatCurrency, formatPercent } from '../../lib/formatters'
import { STATE_TAX_DATA } from '../../lib/calculations'
import { calculateSelfEmploymentTax } from '../../lib/calculations/selfEmploymentTax'
import { trackExportReport } from '../../lib/analytics'
import GlassCard from '../ui/GlassCard'
import StatCard from '../ui/StatCard'
import Button from '../ui/Button'
import { useSubscription } from '../../context/SubscriptionContext'

function InputField({ label, value, onChange, prefix, suffix, type = 'number', step, min, max, hint }) {
  return (
    <div>
      <label className="block text-xs font-medium text-muted mb-1.5">{label}</label>
      <div className="relative">
        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-faint text-sm">{prefix}</span>}
        <input
          type={type}
          value={value}
          onChange={e => onChange(type === 'number' ? (parseFloat(e.target.value) || 0) : e.target.value)}
          step={step}
          min={min}
          max={max}
          className={`w-full bg-input border border-input rounded-xl py-2.5 text-sm text-heading focus:outline-none focus:border-primary transition ${prefix ? 'pl-7' : 'pl-3'} ${suffix ? 'pr-8' : 'pr-3'}`}
        />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-faint text-sm">{suffix}</span>}
      </div>
      {hint && <p className="text-xs text-faint mt-1">{hint}</p>}
    </div>
  )
}

function ResultCard({ label, value, color, highlight }) {
  return (
    <GlassCard className={`!p-4 ${highlight ? 'ring-1' : ''}`} style={highlight ? { borderColor: color + '44' } : {}}>
      <p className="text-xs text-muted mb-1">{label}</p>
      <p className={`font-bold ${highlight ? 'text-xl' : 'text-lg'}`} style={{ color }}>{value}</p>
    </GlassCard>
  )
}

export default function SelfEmploymentTax() {
  const { state } = useFinance()
  const { isPro } = useSubscription()
  const colors = getThemeColors(state.theme)

  const [grossIncome, setGrossIncome] = useState(100000)
  const [businessExpenses, setBusinessExpenses] = useState(15000)
  const [filingStatus, setFilingStatus] = useState('single')
  const [otherW2Income, setOtherW2Income] = useState(0)
  const [stateCode, setStateCode] = useState('OH')
  const [isSSTB, setIsSSTB] = useState(false)
  const [w2WagesPaidByBiz, setW2WagesPaidByBiz] = useState(0)
  const [ubiaQualifiedProperty, setUbiaQualifiedProperty] = useState(0)

  const result = useMemo(() => calculateSelfEmploymentTax({
    grossIncome, businessExpenses, filingStatus, otherW2Income, stateCode,
    isSSTB, w2WagesPaidByBiz, ubiaQualifiedProperty,
  }), [grossIncome, businessExpenses, filingStatus, otherW2Income, stateCode, isSSTB, w2WagesPaidByBiz, ubiaQualifiedProperty])

  const filingStatusLabels = { single: 'Single', mfj: 'Married Filing Jointly', hoh: 'Head of Household' }

  const handleExportPDF = async () => {
    trackExportReport()
    const { default: jsPDF } = await import('jspdf')
    const { default: autoTable } = await import('jspdf-autotable')

    const doc = new jsPDF()
    const pageWidth = doc.internal.pageSize.getWidth()
    const pageHeight = doc.internal.pageSize.getHeight()
    const margin = 14
    const contentWidth = pageWidth - margin * 2

    const checkPageBreak = (y, needed = 40) => {
      if (y + needed > pageHeight - margin) {
        doc.addPage()
        return margin + 4
      }
      return y
    }

    // ── Title ──
    doc.setFillColor(255, 255, 255)
    doc.rect(0, 0, pageWidth, pageHeight, 'F')
    doc.setFontSize(22)
    doc.setTextColor(30, 30, 30)
    doc.text('Self-Employment Tax Report', margin, 22)
    doc.setFontSize(10)
    doc.setTextColor(120, 120, 120)
    doc.text(`Generated by Liquid Finance  |  ${new Date().toLocaleDateString()}`, margin, 30)
    doc.setDrawColor(200, 200, 200)
    doc.line(margin, 33, pageWidth - margin, 33)

    // ── Section 1: Self-Employment Income (Input Summary) ──
    let y = 40
    doc.setFontSize(14)
    doc.setTextColor(30, 30, 30)
    doc.text('Self-Employment Income', margin, y)
    y += 4

    const stateEntry = STATE_TAX_DATA.find(s => s.code === stateCode)
    const stateName = stateEntry ? `${stateEntry.name} (${stateEntry.rate}%)` : stateCode

    autoTable(doc, {
      startY: y,
      head: [['Input', 'Value']],
      body: [
        ['Gross SE Income', formatCurrency(grossIncome)],
        ['Business Expenses', formatCurrency(businessExpenses)],
        ['Net SE Income', formatCurrency(result.netSEIncome)],
        ['SE Taxable Income (92.35% of Net)', formatCurrency(result.seTaxableIncome)],
        ['Other W-2 Income', formatCurrency(otherW2Income)],
        ['Combined Total Income', formatCurrency(result.combinedIncome)],
        ['Filing Status', filingStatusLabels[filingStatus] || filingStatus],
        ['State', stateName],
        ['Specified Service Business (SSTB)', isSSTB ? 'Yes' : 'No'],
        ['W-2 Wages Paid by Business', formatCurrency(w2WagesPaidByBiz)],
        ['UBIA of Qualified Property', formatCurrency(ubiaQualifiedProperty)],
      ],
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold', fontSize: 10 },
      bodyStyles: { fontSize: 9.5, textColor: [40, 40, 40] },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { left: margin, right: margin },
    })

    // ── Section 2: Key Results ──
    y = (doc.lastAutoTable?.finalY || 110) + 12
    y = checkPageBreak(y, 50)
    doc.setFontSize(14)
    doc.setTextColor(30, 30, 30)
    doc.text('Key Results', margin, y)
    y += 4

    autoTable(doc, {
      startY: y,
      head: [['Metric', 'Value']],
      body: [
        ['Total Tax Liability', formatCurrency(result.totalTax)],
        ['Quarterly Estimated Payment', formatCurrency(result.quarterlyPayment)],
        ['Effective Tax Rate', formatPercent(result.effectiveRate)],
        ['Marginal Federal Tax Rate', `${result.marginalRate}%`],
        ['Taxable Income (after deductions)', formatCurrency(result.taxableIncome)],
        ['Take-Home Income', formatCurrency(result.takeHome)],
      ],
      theme: 'grid',
      headStyles: { fillColor: [34, 197, 94], textColor: 255, fontStyle: 'bold', fontSize: 10 },
      bodyStyles: { fontSize: 9.5, textColor: [40, 40, 40] },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { left: margin, right: margin },
    })

    // ── Section 3: SE Tax Visual Breakdown (Bar chart recreated as table with percentages) ──
    y = (doc.lastAutoTable?.finalY || 150) + 12
    y = checkPageBreak(y, 80)
    doc.setFontSize(14)
    doc.setTextColor(30, 30, 30)
    doc.text('Tax Breakdown', margin, y)
    y += 4

    const breakdownItems = [
      { label: 'Net SE Income', amount: result.netSEIncome, total: result.combinedIncome },
    ]
    // SE Tax components
    breakdownItems.push(
      { label: 'SE Tax (Social Security)', amount: result.ssTax, total: result.totalTax },
      { label: 'SE Tax (Medicare)', amount: result.medicareTax, total: result.totalTax },
    )
    if (result.additionalMedicareTax > 0) {
      breakdownItems.push({ label: 'Additional Medicare Tax', amount: result.additionalMedicareTax, total: result.totalTax })
    }
    breakdownItems.push(
      { label: 'Total Self-Employment Tax', amount: result.totalSETax, total: result.totalTax },
    )
    // Income tax components
    breakdownItems.push(
      { label: 'Federal Income Tax', amount: result.federalIncomeTax, total: result.totalTax },
    )
    if (result.stateTax > 0) {
      breakdownItems.push({ label: `State Tax (${result.stateRate}%)`, amount: result.stateTax, total: result.totalTax })
    }
    breakdownItems.push(
      { label: 'Total Tax', amount: result.totalTax, total: result.totalTax },
    )

    const breakdownBody = breakdownItems.map(item => {
      const pct = item.total > 0 ? ((item.amount / item.total) * 100).toFixed(1) : '0.0'
      return [item.label, formatCurrency(item.amount), `${pct}%`]
    })

    autoTable(doc, {
      startY: y,
      head: [['Component', 'Amount', '% of Total Tax']],
      body: breakdownBody,
      theme: 'striped',
      headStyles: { fillColor: [239, 68, 68], textColor: 255, fontStyle: 'bold', fontSize: 10 },
      bodyStyles: { fontSize: 9.5, textColor: [40, 40, 40] },
      alternateRowStyles: { fillColor: [254, 242, 242] },
      margin: { left: margin, right: margin },
      columnStyles: {
        1: { halign: 'right' },
        2: { halign: 'right' },
      },
      didParseCell: function(data) {
        // Bold the total rows
        if (data.row.index === breakdownBody.length - 1 || data.row.raw[0] === 'Total Self-Employment Tax') {
          data.cell.styles.fontStyle = 'bold'
        }
      },
    })

    // ── Section 4: Deductions Applied ──
    y = (doc.lastAutoTable?.finalY || 200) + 12
    y = checkPageBreak(y, 60)
    doc.setFontSize(14)
    doc.setTextColor(30, 30, 30)
    doc.text('Deductions Applied', margin, y)
    y += 4

    const standardDeduction = result.totalDeductions - result.seDeduction - result.qbiDeduction

    autoTable(doc, {
      startY: y,
      head: [['Deduction', 'Amount']],
      body: [
        ['Half of SE Tax (above-the-line deduction)', formatCurrency(result.seDeduction)],
        [`QBI Deduction${result.qbiLimited ? ' (limited)' : ' (20% of QBI)'}`, formatCurrency(result.qbiDeduction)],
        ['Standard Deduction', formatCurrency(standardDeduction)],
        ['Total Deductions', formatCurrency(result.totalDeductions)],
      ],
      theme: 'grid',
      headStyles: { fillColor: [139, 92, 246], textColor: 255, fontStyle: 'bold', fontSize: 10 },
      bodyStyles: { fontSize: 9.5, textColor: [40, 40, 40] },
      alternateRowStyles: { fillColor: [245, 243, 255] },
      margin: { left: margin, right: margin },
      columnStyles: { 1: { halign: 'right' } },
      didParseCell: function(data) {
        if (data.row.index === 3) {
          data.cell.styles.fontStyle = 'bold'
        }
      },
    })

    // ── Section 5: QBI Deduction Explanation ──
    y = (doc.lastAutoTable?.finalY || 230) + 10
    y = checkPageBreak(y, 40)
    doc.setFontSize(12)
    doc.setTextColor(100, 60, 160)
    doc.text('QBI Deduction Details', margin, y)
    y += 7
    doc.setFontSize(9)
    doc.setTextColor(70, 70, 70)

    let qbiExplanation
    if (result.qbiDeduction === 0 && result.netSEIncome <= 0) {
      qbiExplanation = 'No QBI deduction is available because your net self-employment income is zero or negative.'
    } else if (result.qbiLimited && isSSTB) {
      qbiExplanation = `As a Specified Service Trade or Business (SSTB), your QBI deduction is limited or phased out based on your taxable income. Your QBI deduction of ${formatCurrency(result.qbiDeduction)} reflects this limitation. SSTBs include fields such as law, health, consulting, and financial services.`
    } else if (result.qbiLimited) {
      qbiExplanation = `Your QBI deduction is limited based on W-2 wages paid (${formatCurrency(w2WagesPaidByBiz)}) and/or UBIA of qualified property (${formatCurrency(ubiaQualifiedProperty)}). The deduction of ${formatCurrency(result.qbiDeduction)} reflects the W-2/UBIA limitation that applies when taxable income exceeds the threshold.`
    } else {
      qbiExplanation = `You qualify for the full 20% QBI deduction on your net self-employment income of ${formatCurrency(result.netSEIncome)}. This deduction of ${formatCurrency(result.qbiDeduction)} reduces your taxable income but not your self-employment tax.`
    }

    const qbiLines = doc.splitTextToSize(qbiExplanation, contentWidth)
    doc.text(qbiLines, margin, y)
    y += qbiLines.length * 4.5 + 4

    // ── Section 6: Quarterly Estimated Payment Schedule ──
    y = checkPageBreak(y, 70)
    doc.setFontSize(14)
    doc.setTextColor(30, 30, 30)
    doc.text('Quarterly Estimated Payment Schedule', margin, y)
    y += 2
    doc.setFontSize(9)
    doc.setTextColor(100, 100, 100)
    doc.text('IRS Form 1040-ES due dates for estimated tax payments', margin, y + 4)
    y += 8

    const currentYear = new Date().getFullYear()
    const nextYear = currentYear + 1

    autoTable(doc, {
      startY: y,
      head: [['Quarter', 'Due Date', 'Amount']],
      body: [
        ['Q1', `April 15, ${currentYear}`, formatCurrency(result.quarterlyPayment)],
        ['Q2', `June 15, ${currentYear}`, formatCurrency(result.quarterlyPayment)],
        ['Q3', `September 15, ${currentYear}`, formatCurrency(result.quarterlyPayment)],
        ['Q4', `January 15, ${nextYear}`, formatCurrency(result.quarterlyPayment)],
        ['Annual Total', '', formatCurrency(result.quarterlyPayment * 4)],
      ],
      theme: 'grid',
      headStyles: { fillColor: [245, 158, 11], textColor: 255, fontStyle: 'bold', fontSize: 10 },
      bodyStyles: { fontSize: 9.5, textColor: [40, 40, 40] },
      alternateRowStyles: { fillColor: [255, 251, 235] },
      margin: { left: margin, right: margin },
      columnStyles: { 2: { halign: 'right' } },
      didParseCell: function(data) {
        if (data.row.index === 4) {
          data.cell.styles.fontStyle = 'bold'
        }
      },
    })

    // ── Section 7: Income Flow Summary ──
    y = (doc.lastAutoTable?.finalY || y) + 12
    y = checkPageBreak(y, 60)
    doc.setFontSize(14)
    doc.setTextColor(30, 30, 30)
    doc.text('Income Flow Summary', margin, y)
    y += 4

    autoTable(doc, {
      startY: y,
      head: [['Step', 'Amount']],
      body: [
        ['Gross SE Income', formatCurrency(grossIncome)],
        ['- Business Expenses', `(${formatCurrency(businessExpenses)})`],
        ['= Net SE Income', formatCurrency(result.netSEIncome)],
        ['+ Other W-2 Income', formatCurrency(otherW2Income)],
        ['= Combined Income', formatCurrency(result.combinedIncome)],
        ['- Total Deductions', `(${formatCurrency(result.totalDeductions)})`],
        ['= Taxable Income', formatCurrency(result.taxableIncome)],
        ['- Total Tax', `(${formatCurrency(result.totalTax)})`],
        ['= Take-Home Income', formatCurrency(result.takeHome)],
      ],
      theme: 'grid',
      headStyles: { fillColor: [16, 185, 129], textColor: 255, fontStyle: 'bold', fontSize: 10 },
      bodyStyles: { fontSize: 9.5, textColor: [40, 40, 40] },
      alternateRowStyles: { fillColor: [240, 253, 244] },
      margin: { left: margin, right: margin },
      columnStyles: { 1: { halign: 'right' } },
      didParseCell: function(data) {
        // Bold the result rows
        const boldRows = [2, 4, 6, 8]
        if (boldRows.includes(data.row.index)) {
          data.cell.styles.fontStyle = 'bold'
        }
      },
    })

    // ── Disclaimer ──
    y = (doc.lastAutoTable?.finalY || y) + 10
    y = checkPageBreak(y, 30)
    doc.setFontSize(8)
    doc.setTextColor(150, 150, 150)
    const disclaimerLines = doc.splitTextToSize(
      'Disclaimer: This report is for informational purposes only and does not constitute tax advice. Self-employment tax calculations are estimates based on 2025 federal tax brackets, FICA rates, and QBI thresholds. The 92.35% SE taxable income factor, Social Security wage base, and Medicare rates are based on current IRS guidelines. Quarterly estimated payments are calculated as one-fourth of total annual tax liability. Actual tax liability may vary based on your complete tax situation. Consult a qualified tax professional for personalized advice.',
      contentWidth
    )
    doc.text(disclaimerLines, margin, y)

    doc.save(`self-employment-tax-${Date.now()}.pdf`)
  }

  const filingOptions = [
    { value: 'single', label: 'Single' },
    { value: 'mfj', label: 'Married Filing Jointly' },
    { value: 'hoh', label: 'Head of Household' },
  ]

  return (
    <div className="space-y-6">
      {/* Inputs */}
      <GlassCard>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h3 className="text-lg font-semibold text-heading">Self-Employment Income</h3>
          <Button variant="secondary" size="sm" onClick={handleExportPDF} disabled={!isPro} title={!isPro ? 'Pro feature' : ''}>
            Export PDF{!isPro ? ' (Pro)' : ''}
          </Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <InputField label="Gross SE Income" value={grossIncome} onChange={setGrossIncome} prefix="$" />
          <InputField label="Business Expenses" value={businessExpenses} onChange={setBusinessExpenses} prefix="$" />
          <InputField label="Other W-2 Income" value={otherW2Income} onChange={setOtherW2Income} prefix="$" hint="From an employer, if any" />
          <div>
            <label className="block text-xs font-medium text-muted mb-1.5">Filing Status</label>
            <div className="flex gap-1">
              {filingOptions.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setFilingStatus(opt.value)}
                  className={`flex-1 px-2 py-2 rounded-xl text-xs font-medium transition-all ${
                    filingStatus === opt.value ? 'text-heading shadow-sm' : 'text-muted hover:text-heading'
                  }`}
                  style={filingStatus === opt.value ? {
                    background: `linear-gradient(135deg, ${colors.primary}22, ${colors.secondary}22)`,
                    border: `1px solid ${colors.primary}44`,
                  } : { border: '1px solid transparent' }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted mb-1.5">State</label>
            <select
              value={stateCode}
              onChange={e => setStateCode(e.target.value)}
              className="w-full bg-input border border-input rounded-xl py-2.5 px-3 text-sm text-heading focus:outline-none focus:border-primary transition"
            >
              {STATE_TAX_DATA.filter(s => s.code !== 'CUSTOM').map(s => (
                <option key={s.code} value={s.code}>{s.name} ({s.rate}%)</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted mb-1.5">Specified Service Business (SSTB)?</label>
            <div className="flex gap-1">
              {[{ value: false, label: 'No' }, { value: true, label: 'Yes' }].map(opt => (
                <button
                  key={String(opt.value)}
                  onClick={() => setIsSSTB(opt.value)}
                  className={`flex-1 px-2 py-2 rounded-xl text-xs font-medium transition-all ${
                    isSSTB === opt.value ? 'text-heading shadow-sm' : 'text-muted hover:text-heading'
                  }`}
                  style={isSSTB === opt.value ? {
                    background: `linear-gradient(135deg, ${colors.primary}22, ${colors.secondary}22)`,
                    border: `1px solid ${colors.primary}44`,
                  } : { border: '1px solid transparent' }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-faint mt-1">Law, health, consulting, financial services, etc.</p>
          </div>
          <InputField label="W-2 Wages Paid" value={w2WagesPaidByBiz} onChange={setW2WagesPaidByBiz} prefix="$" hint="Wages you pay to employees (most sole proprietors: $0)" />
          <InputField label="UBIA of Qualified Property" value={ubiaQualifiedProperty} onChange={setUbiaQualifiedProperty} prefix="$" hint="Unadjusted basis of business property" />
        </div>
      </GlassCard>

      {/* Key Results */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <ResultCard label="Total Tax" value={formatCurrency(result.totalTax)} color={colors.negative} highlight />
        <ResultCard label="Quarterly Payment" value={formatCurrency(result.quarterlyPayment)} color={colors.warning} highlight />
        <ResultCard label="Effective Rate" value={formatPercent(result.effectiveRate)} color={colors.primary} />
        <ResultCard label="Take-Home" value={formatCurrency(result.takeHome)} color={colors.positive} highlight />
      </div>

      {/* Tax Breakdown */}
      <GlassCard>
        <h3 className="text-lg font-semibold text-heading mb-4">Tax Breakdown</h3>
        <div className="space-y-3">
          <BreakdownRow label="Net SE Income" amount={result.netSEIncome} color={colors.primary} total={result.combinedIncome} />
          <div className="border-t border-card my-2" />
          <BreakdownRow label="SE Tax (Social Security)" amount={result.ssTax} color={colors.negative} total={result.totalTax} />
          <BreakdownRow label="SE Tax (Medicare)" amount={result.medicareTax} color={colors.negative} total={result.totalTax} />
          {result.additionalMedicareTax > 0 && (
            <BreakdownRow label="Additional Medicare Tax" amount={result.additionalMedicareTax} color={colors.warning} total={result.totalTax} />
          )}
          <BreakdownRow label="Total Self-Employment Tax" amount={result.totalSETax} color={colors.negative} total={result.totalTax} />
          <div className="border-t border-card my-2" />
          <BreakdownRow label="Federal Income Tax" amount={result.federalIncomeTax} color={colors.secondary} total={result.totalTax} />
          {result.stateTax > 0 && (
            <BreakdownRow label={`State Tax (${result.stateRate}%)`} amount={result.stateTax} color={colors.warning} total={result.totalTax} />
          )}
          <div className="border-t border-card my-2" />
          <div className="flex justify-between items-center">
            <span className="text-sm font-semibold text-heading">Total Tax</span>
            <span className="text-sm font-bold" style={{ color: colors.negative }}>{formatCurrency(result.totalTax)}</span>
          </div>
        </div>
      </GlassCard>

      {/* Deductions */}
      <GlassCard>
        <h3 className="text-lg font-semibold text-heading mb-4">Deductions Applied</h3>
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted">Half of SE Tax (deductible)</span>
            <span className="text-heading">{formatCurrency(result.seDeduction)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted">QBI Deduction{result.qbiLimited ? ' (limited)' : ' (20%)'}</span>
            <span className="text-heading">{formatCurrency(result.qbiDeduction)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted">Standard Deduction</span>
            <span className="text-heading">{formatCurrency(result.totalDeductions - result.seDeduction - result.qbiDeduction)}</span>
          </div>
          <div className="border-t border-card pt-2 flex justify-between text-sm font-semibold">
            <span className="text-heading">Total Deductions</span>
            <span style={{ color: colors.positive }}>{formatCurrency(result.totalDeductions)}</span>
          </div>
        </div>
      </GlassCard>
    </div>
  )
}

function BreakdownRow({ label, amount, color, total }) {
  const pct = total > 0 ? (amount / total) * 100 : 0
  return (
    <div className="flex items-center gap-3">
      <span className="text-sm text-muted flex-1">{label}</span>
      <div className="w-24 h-2 rounded-full bg-subtle overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(100, pct)}%`, backgroundColor: color }} />
      </div>
      <span className="text-sm font-medium text-heading w-20 text-right">{formatCurrency(amount)}</span>
    </div>
  )
}
